import { useTheme } from "@mui/material";
import { Licensing_V1 } from "@rw1/auto-generated-clients";
import { FC, useState } from "react";
import { ConfirmationDialog, SeatPicker } from "../../../components";
import { useLocalization } from "../../../hooks/useLocalization";
import { LicenseWithExperienceName } from "../hooks/useLicenseById";
import { useUpdateLicense } from "../hooks/useUpdateLicense";

interface EditSeatsDialogProps {
  open: boolean;
  onClose: () => void;
  license: LicenseWithExperienceName;
}

export const EditSeatsDialog: FC<EditSeatsDialogProps> = ({
  open,
  onClose,
  license,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const [seats, setSeats] = useState<number | null>(license.seats ?? null);

  const [checked, setChecked] = useState(false);

  const updateLicenseMutation = useUpdateLicense();

  const handleSwitchChange = (isChecked: boolean) => {
    setChecked(isChecked);
  };

  const handleSeatsChange = (newSeats: number | null) => {
    setSeats(newSeats);
  };

  const handleCancel = () => {
    setSeats(license.seats ?? null);
    onClose();
    setChecked(false);
    updateLicenseMutation.reset();
  };

  const isApplyDisabled =
    (!checked &&
      (seats === null || seats < license.allocatedSeats || seats === 0)) ||
    updateLicenseMutation.isPending;

  const onUpdateSeats = () => {
    const newSeats = checked || !seats ? undefined : seats;

    const updatedLicense = new Licensing_V1.LicenseBase({
      ...license,
      seats: newSeats,
    });

    updateLicenseMutation.mutate({
      licenseId: license.licenseId,
      licenseData: updatedLicense,
      onSuccess: onClose,
    });
  };

  return (
    <ConfirmationDialog.Root
      fullScreenOnMobile
      maxWidth="xs"
      onClose={handleCancel}
      open={open}
    >
      <ConfirmationDialog.Title showIcon={true}>
        {t("licenses.editSeatsDialog.title")}
      </ConfirmationDialog.Title>
      <ConfirmationDialog.Content
        error={updateLicenseMutation.error}
        sx={{ paddingBottom: "20px" }}
      >
        <ConfirmationDialog.Text>
          {t("licenses.editSeatsDialog.description")}
        </ConfirmationDialog.Text>
        <SeatPicker
          checked={checked}
          handleSwitchChange={handleSwitchChange}
          maxLength={4}
          min={license.allocatedSeats}
          mobileLabel={t("licenses.editSeatsDialog.mobileLabel")}
          onChange={handleSeatsChange}
          value={seats}
        />
      </ConfirmationDialog.Content>
      <ConfirmationDialog.Actions>
        <ConfirmationDialog.Button
          onClick={handleCancel}
          sx={{
            color: theme.colors.primary[5],
          }}
          variant="secondary"
        >
          {t("licenses.editSeatsDialog.cancel")}
        </ConfirmationDialog.Button>
        <ConfirmationDialog.Button
          disabled={isApplyDisabled}
          isLoading={updateLicenseMutation.isPending}
          loadingText={t("licenses.editSeatsDialog.applying")}
          onClick={onUpdateSeats}
          variant="contained"
        >
          {t("licenses.editSeatsDialog.apply")}
        </ConfirmationDialog.Button>
      </ConfirmationDialog.Actions>
    </ConfirmationDialog.Root>
  );
};
